import * as _ from "lodash";
import * as angular from "angular";
import { Campaign } from "../../../models/campaign.model";
import { Feature } from "../../../models/feature.model";
import { Organisation } from "../../../models/organisation.model";
import { OrganisationService } from "../../../services/organisation.service";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";
import {
    languagesConstants,
    LanguageCode,
} from "../../../utils/constants/languages.constants";

let CreateCampaignComponent = {
    selector: "vcAdminCreateCampaign",
    templateUrl:
        "/angular/components/admin/dialogs/create-campaign-dialog.component.html",
    bindings: {
        /**
         * AV-18115: opened from an organisation row, the organisation is
         * already known. Skips the "what are you creating" step and
         * preselects it.
         */
        presetOrganisation: "<",
    },
    controller: class CreateCampaignController {
        // * bindings for typescript compiler *
        public presetOrganisation?: { oid: number | string; oname: string };
        // ************************************

        private $scope: any;
        private $http: any;
        private $window: any;
        private dialog: DialogUtils;
        private toast: ToastUtils;
        private organisationService: OrganisationService;
        public campaign: Campaign;
        public features = [
            { index: "EXPORT", name: "Export", owner: 1 },
            { index: "TURF_CUTTING", name: "Turf Cutting", owner: 1 },
            {
                index: "CANVASSER_TRACKING_ALLOWED",
                name: "Field Tracking Allowed",
                owner: 1,
            },
            { index: "GEO_CODING", name: "Geocoding", owner: 1 },
            // Off for new accounts; an admin switches it on deliberately.
            { index: "USERLIMIT", name: "User Licenses", owner: 0 },
            { index: "2FA", name: "Two Factor Authentication", owner: 0 },
            { index: "PUBLIC_API", name: "Public API", owner: 0 },
            {
                index: "GO_APP",
                name: "Go App (Whether app selection drop downs display in places)",
                owner: 0,
            },
        ];
        public loading = false;
        public langCodes: LanguageCode[] = languagesConstants.languages;
        public selectedLangCode: LanguageCode | null = null;
        public langCodeSearch: string = "";
        public organisations: Organisation[] = [];
        public __env: any;
        public label = "Select Organization";
        public type: string;
        /** Rail sections for the shell; built once the account kind is chosen. */
        public sections: { id: string; label: string; icon: string }[] = [];
        public search = "";

        constructor(
            $scope: any,
            $http: any,
            $window: any,
            __env: any,
            OrganisationService: OrganisationService,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            __app_type: string,
            ModelsUtils: any
        ) {
            this.$scope = $scope;
            this.$http = $http;
            this.$window = $window;
            this.__env = __env;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.organisationService = OrganisationService;
            this.campaign = ModelsUtils.campaign({
                limits: {
                    superusers: 2,
                    users: 20,
                    customfields: 5,
                    houseoccupants: 100000,
                    extrasuperusers: 0,
                    extrausers: 0,
                    extracustomfields: 0,
                },
            });

            // Bindings are not set until $onInit, so the organisation list
            // is fetched there instead: opened from an organisation row the
            // target is already known and the request is wasted.
        }

        $onInit() {
            // AV-18115: launched from an organisation's row menu, so the
            // only thing being created is a sub account of that
            // organisation. Preselect it and start at the form.
            if (this.presetOrganisation && this.presetOrganisation.oid) {
                this.chooseType("leader");
                this.campaign.organisation = this
                    .presetOrganisation as Organisation;
            } else {
                this.loadOrganisations();
            }

            if (this.campaign.langcode) {
                this.selectedLangCode =
                    this.langCodes.find(
                        (language: LanguageCode): boolean =>
                            language.code === this.campaign.langcode
                    ) ?? null;
            }

            this.$scope.$watch(
                () => this.campaign.plan,
                (newValue: string, oldValue: object | string) => {
                    if (newValue != oldValue) {
                        this.setFeaturesBasedOnPlan(newValue);
                    }
                },
                true
            );
        }

        setFeaturesBasedOnPlan(plan): void {
            if (plan.features) {
                angular.forEach(this.features, (feature: Feature) => {
                    // User licences stay off however the plan is set up.
                    if (feature.index == "USERLIMIT") return;
                    for (let [key, value] of Object.entries(plan.features) as [
                        any,
                        any
                    ]) {
                        if (feature.index == key) {
                            feature.owner = value.enabled ? true : false;
                        }
                    }
                });
            }
        }

        loadOrganisations(): void {
            this.organisationService.admin().then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.organisations = json.data;
                }
            });
        }

        filterLangCodes(query: string): LanguageCode[] {
            const lowerQuery: string = query ? query.toLowerCase() : "";
            return lowerQuery
                ? this.langCodes.filter(
                      (language: LanguageCode): boolean =>
                          language.code.toLowerCase().includes(lowerQuery) ||
                          language.name.toLowerCase().includes(lowerQuery)
                  )
                : this.langCodes;
        }

        filterOrganisations(query: string): Organisation[] {
            return query && query != ""
                ? this.organisations.filter((obj: Organisation) => {
                      return (
                          obj.oname
                              .toLowerCase()
                              .indexOf(query.toLowerCase()) != -1
                      );
                  })
                : this.organisations;
        }

        saveCampaign(): void {
            if (this.loading == false) {
                this.loading = true;
                let data = _.cloneDeep(this.campaign);
                if (this.campaign.organisation) {
                    data.oid = this.campaign.organisation.oid;
                }
                data.langcode = this.selectedLangCode
                    ? this.selectedLangCode.code
                    : null;
                data.features = this.features;
                this.$http
                    .post("/v2/user/sandboxRegister", data)
                    .then((response: object) => {
                        const json = response["data"];
                        if (json.success) {
                            // We want to include Sandbox Campaigns in GA Signup Goals
                            this.$window.ga(
                                "create",
                                this.__env.ga_key,
                                "auto"
                            );
                            this.$window.ga("send", {
                                hitType: "event",
                                eventCategory: "signup",
                                eventAction: "sandbox",
                                eventLabel: "Sandbox Completed",
                                eventValue: 1,
                            });
                            this.toast.success("Account created");
                            this.dialog.hide(json.data);
                        } else {
                            this.toast.error();
                            this.dialog.hide(json);
                        }
                        this.loading = false;
                    });
            }
        }

        /**
         * Standard accounts need an owner, payment and limits; organisation
         * sub accounts only pick the organisation and the account names.
         */
        chooseType(type: "account" | "leader"): void {
            // Creating scrolls as one page: no rail, every section stacked.
            this.sections = [];
            this.type = type;
        }

        headingText(): string {
            if (this.presetOrganisation && this.presetOrganisation.oname) {
                return "New account in " + this.presetOrganisation.oname;
            }
            return this.type == "leader"
                ? "New organisation sub account"
                : "New standard account";
        }

        cancel(): void {
            this.dialog.cancel();
        }
    },
};

angular
    .module("admin")
    .component(CreateCampaignComponent.selector, CreateCampaignComponent);
